import { useQuery, useQueryClient } from '@tanstack/react-query'
import { CellContext } from '@tanstack/react-table'
import to from 'await-to-js'
import { DataTable, useDataTableContext } from 'components/DataTable'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { ColumnSelectorRecipient } from 'components/Modals/ColumnModal/ColumnModal'
import * as S from 'components/Tables/CommonTable.styles'
import dialogConfirm from 'components/dialogConfirm'
import { ContactResponse } from 'models/contacts'
import { Deal } from 'models/deals'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { OverlayTrigger } from 'react-bootstrap'
import {
  BsDownload,
  BsPencil,
  BsPerson,
  BsSignpost,
  BsTags,
  BsTruck,
  BsXCircle,
} from 'react-icons/bs'
import { toast } from 'react-toastify'
import useApiService from 'services/api'
import { IContactCompany } from '../../../models/contact_companies'
import { FilterIdentifier } from '../../../models/saved_view'

import { getTableQueryKey } from '../../../utils/getTableQueryKey'
import {
  ActionsDropdown,
  createDeleteAction,
  createDownloadAction,
  createEditAction,
} from '../../Buttons/ActionsDropdown'
import TableButtonAction from '../../Buttons/TableButtons/TableButtonAction'
import { dataTableSortingStoreRepo } from '../../DataTable/DataTableSorting/DataTableSortingStore'
import { DataTableContainer } from '../../DataTable/UI'
import { EditDealModal } from '../../Deals/EditDealModal/EditDealModal'

import { TableSearch } from '../../Filters/TableSearch'
import { FilterChips } from '../../Filters/components/FilterChips/FilterChips'
import DistributorAssignModal from '../../Modals/AssignmentModal/DistributorAssignModal'
import GPOAssignModal from '../../Modals/AssignmentModal/GPOAssignModal'
import TagAssignModal from '../../Modals/AssignmentModal/TagAssignModal'
import DealExportModal from '../../Modals/ExportModal/DealExportModal'
import DealsOverrideModal from '../../Modals/FormActionModal/DealsOverrideModal'
import AccountOwnerSelectModal from '../../Modals/SelectModal/AccountOwnerSelectModal'
import SalesStageSelectModal from '../../Modals/SelectModal/SalesStageSelectModal'
import { cn } from '../../UI/cn'
import { DealsFilterset } from './DealsFilterset'
import { useDealsTableColumns } from './useDealsTableColumns'
import { useBulkDealsDeleter } from './useBulkDealsDeleter'
import { PiShippingContainer, PiCalendarStar } from 'react-icons/pi'
import { BsFileEarmarkPdf } from 'react-icons/bs'
import { ExportButton, PAGE_SIZE, tooltip } from './DealsTable'
import ReminderModal from '../../Reminders/ReminderModal'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { DealsTableBaseFilters } from './DealsTableTypes'
import { useDownload } from '../../../features/download-widget'
import {
  bulkDownloadSpecsheetsFromDeals,
  bulkDownloadSpecsheetsOptimized,
  getBulkDownloadStatus,
} from 'services/products'

type SortableFields = (keyof ContactResponse | string)[]

const sortableFields: SortableFields = [
  'id',
  'identifier',
  'company',
  'deal_name',
  'product',
  'product_variant',

  'monthly_volume_predicted',
  'monthly_volume_override',
  'monthly_revenue_predicted',
  'monthly_revenue_override',
  'annual_volume',
  'annual_revenue',
  'annual_total',
  'annual_brand_opp',

  'close_date_override',
  'sales_stage',
  'account_owner',
  'created',
  'modified',
  'last_datetime_stage_changed',
  'distributors',
  'expected_days_to_close',
  'note_count',
  'category',
  'sub_category',
  'contact_count',
  'contact_tags',
  'days_since_last_modified',
  'company_size',
  'estimated_meals_per_day',
  'days_since_sent',
  'days_since_reply',
  'days_since_deal_last_modified',
  'doors_count',
]

interface DealsTableProps {
  setTotalRowsCount?: (count: number) => void
  forCompany?: Partial<IContactCompany>
  forDoorProxyId?: number
  showCompanyColumn?: boolean
  enablePagination?: boolean
  disableUrlPagination?: boolean
  baseFilters?: DealsTableBaseFilters
  tableKey: string
  filterIdentifierModifier?: string
}

export function DealsTableComponent(props: DealsTableProps) {
  const api = useApiService()
  const queryClient = useQueryClient()
  const featureFlag = useFeatureFlagService()

  const { sorting } = dataTableSortingStoreRepo.getStore(props.tableKey)()
  const {
    state: { totalSelectedRows, rowSelection },
    methods: { clearSelectedRows, setTotalRowsInBackend },
  } = useDataTableContext()

  const [dealIdToEdit, setDealIdToEdit] = useState<number>()
  const [showAssignTags, setShowAssignTags] = useState(false)
  const [showAssignDealTags, setShowAssignDealTags] = useState(false)
  const [showAssignDistributors, setShowAssignDistributors] = useState(false)
  const [showAssignGPOs, setShowAssignGPOs] = useState(false)
  const [showSelectAccountOwner, setShowSelectAccountOwner] = useState(false)
  const [showSelectSalesStage, setShowSelectSalesStage] = useState(false)
  const [showOverrideModal, setShowOverrideModal] = useState(false)
  const [showExportModal, setShowExportModal] = React.useState(false)
  const [showReminderModal, setShowReminderModal] = useState(false)
  const IDENTIFIER = ('DEALS_TABLE' +
    (props.filterIdentifierModifier ?? '')) as FilterIdentifier
  const dealsFilterStore = filterStoreRepo.getStore(IDENTIFIER)
  const { page, setPage } = dealsFilterStore()
  const filtersAsParams = {
    ...props.baseFilters,
    ...useFilterParams(dealsFilterStore),
  }

  const filterParams = useMemo(() => {
    const params: Record<string, unknown> = { ...filtersAsParams }

    if (props.forDoorProxyId) {
      params['door_proxy_id'] = props.forDoorProxyId
      params['door_only'] = true
      // Include LTV data for company/door page views
      params['include_ltv'] = true
    } else if (props.forCompany) {
      params['company'] = props.forCompany.id
      // Include LTV data for company/door page views
      params['include_ltv'] = true
    } else if (props.baseFilters?.origin_campaign) {
      // Include LTV for campaign deal views (uses LTV 1.5 when enabled server-side).
      params['include_ltv'] = true
    }
    return params
  }, [filtersAsParams, props.forCompany, props.forDoorProxyId])

  const sortParams = useMemo(() => {
    const params: Record<string, unknown> = {}

    if (sorting?.length) {
      params['sort'] = sorting[0].desc ? '-' + sorting[0]?.id : sorting[0]?.id
    }

    return params
  }, [sorting])

  const filterAndSortParams = useMemo(
    () => ({
      ...filterParams,
      ...sortParams,
    }),
    [filterParams, sortParams]
  )

  const TABLE_QUERY_KEY = getTableQueryKey({
    tableKey: props.tableKey,
    filterParams: filterAndSortParams,
    page: page,
  })

  // DATA FETCHING
  const { isFetching, data, refetch } = useQuery({
    queryKey: TABLE_QUERY_KEY,
    queryFn: ({ signal }) => {
      clearSelectedRows()
      return api.getDeals(
        {
          ...filterAndSortParams,
          page: page,
          limit: PAGE_SIZE,
        },
        signal
      )
    },
  })

  useEffect(() => {
    if (data?.count != null) {
      props.setTotalRowsCount?.(data.count)
      setTotalRowsInBackend(data.count)
    }
  }, [data])

  // END DATA FETCHING

  const { handleSubmit: bulkDeleteDeals, isDeleting } = useBulkDealsDeleter({
    data: data?.results ?? [],
    accessorKey: 'id',
    tableQueryKey: TABLE_QUERY_KEY,
    filterParams: filterAndSortParams,
  })

  // Get selected deal IDs
  const selectedDealIds = useMemo(() => {
    if (!rowSelection || !data?.results) return []
    return Object.keys(rowSelection)
      .filter((key) => rowSelection[key])
      .map((index) => data.results[parseInt(index)]?.id)
      .filter(Boolean)
  }, [rowSelection, data?.results])

  // Get company ID from selected deal
  const selectedCompanyId = useMemo(() => {
    if (selectedDealIds.length === 1 && data?.results) {
      const selectedDeal = data.results.find((d) => d.id === selectedDealIds[0])
      return selectedDeal?.contact_company?.id
    }
    return undefined
  }, [selectedDealIds, data?.results])

  // Bulk download spec sheets
  const { startDownload: startBulkDownload, state: downloadState } =
    useDownload()

  const handleBulkDownloadSpecsheets = useCallback(() => {
    if (!selectedDealIds.length) {
      toast.warning('Please select at least one deal')
      return
    }

    const items = selectedDealIds.map((dealId) => {
      const deal = data?.results?.find((d) => d.id === dealId)
      const displayName = deal?.deal_display_name
        ? `${deal.deal_display_name} (${dealId})`
        : deal?.name
          ? `${deal.name} (${dealId})`
          : deal?.contact_company?.name
            ? `${deal.contact_company.name} (${dealId})`
            : `Deal ${dealId}`
      return { id: dealId, name: displayName }
    })

    startBulkDownload({
      items,
      initiate: async (signal) => {
        const response = await bulkDownloadSpecsheetsFromDeals(
          selectedDealIds,
          signal
        )
        if ('blob' in response)
          return {
            type: 'blob',
            blob: response.blob,
            filename: response.filename,
          }
        return { type: 'async', requestId: response.request_id }
      },
      getStatus: getBulkDownloadStatus,
      label: 'spec sheets',
    })
  }, [selectedDealIds, data?.results, startBulkDownload])

  const handleDownloadSpecsheet = useCallback(
    (deal: Deal) => {
      if (!deal.product_variant_id) return

      // Only allow if not already downloading
      if (
        downloadState.status === 'downloading' ||
        downloadState.status === 'preparing' ||
        downloadState.status === 'pending'
      ) {
        toast.info('A download is already in progress')
        return
      }

      // Use download widget for individual downloads to track progress
      const variantId = deal.product_variant_id
      const dealName = deal.deal_display_name || deal.name || `Deal ${deal.id}`
      startBulkDownload({
        items: [{ id: variantId, name: dealName }],
        initiate: async (signal) => {
          const response = await bulkDownloadSpecsheetsOptimized(
            [variantId],
            signal
          )
          if ('blob' in response)
            return {
              type: 'blob',
              blob: response.blob,
              filename: response.filename,
            }
          return { type: 'async', requestId: response.request_id }
        },
        getStatus: getBulkDownloadStatus,
        label: 'spec sheets',
      })
    },
    [downloadState.status, startBulkDownload]
  )

  async function handleDealDelete(dealId: number) {
    if (
      await dialogConfirm(
        <>
          Are you sure you want to delete this <b>deal</b>? This action cannot
          be undone.
        </>,
        'Delete Deal?',
        '',
        { confirmLabel: 'Delete' }
      )
    ) {
      const [err] = await to(api.deleteDeal(dealId))
      if (!err) {
        toast.success('Deal deleted successfully')
        void refetch()
        if (props.forCompany?.id) {
          queryClient.invalidateQueries({
            queryKey: ['company', props.forCompany.id],
          })
        }
        if (props.forDoorProxyId) {
          queryClient.invalidateQueries({
            queryKey: ['door_proxy', props.forDoorProxyId],
          })
        }
      }
    }
  }

  const columns = [
    ...useDealsTableColumns({
      showCompanyColumn: props.showCompanyColumn,
      sorting,
    }),
    {
      id: 'actions',
      header: 'Actions',
      size: 100,
      meta: {
        isEditable: false,
        headerDisableOrdering: true,
        isLastColumn: true,
        rightAlign: true,
      },
      cell: (info: CellContext<Deal, unknown>) => {
        const deal = info.row.original
        const actions = [
          createEditAction(() => setDealIdToEdit(deal.id)),
          createDeleteAction(() => handleDealDelete(deal.id)),
        ]

        // Add download spec sheet action if the deal has a variant with Syndigo data
        if (featureFlag.enableSyndigoIntegration && deal.product_variant_id) {
          actions.push(
            createDownloadAction('Download Spec Sheet', () =>
              handleDownloadSpecsheet(deal)
            )
          )
        }

        return <ActionsDropdown actions={actions} containerWidth={180} />
      },
    },
  ]

  return (
    <DataTableContainer key={props.tableKey + '-container'}>
      <DataTable
        tableHeader={
          <S.SearchContainer>
            <div className="flex w-full gap-2 items-center">
              <TableSearch
                className={'flex-1'}
                filterStore={dealsFilterStore}
                searchPlaceholder="Search by name, ID, company..."
                tableKey={props.tableKey}
              />
              <DealsFilterset
                filterIdentifier={IDENTIFIER}
                hideCampaignsFilter={!!props.baseFilters?.origin_campaign}
              />
              <ColumnSelectorRecipient tableKey={props.tableKey} />
            </div>

            <div className={cn('flex flex-row justify-between gap-4')}>
              <TableButtonAction
                items={[
                  {
                    label: 'Manage Company Tags',
                    icon: <BsTags size={20} />,
                    callback: () => setShowAssignTags(true),
                  },
                  {
                    label: 'Manage Deal Tags',
                    icon: <BsTags size={20} />,
                    callback: () => setShowAssignDealTags(true),
                  },
                  {
                    label: 'Add Known Distributors',
                    icon: <BsTruck size={20} />,
                    callback: () => setShowAssignDistributors(true),
                  },
                  {
                    label: 'Add GPO',
                    icon: <PiShippingContainer size={20} />,
                    callback: () => setShowAssignGPOs(true),
                  },
                  ...(featureFlag.enableReminders
                    ? [
                        {
                          label: 'Schedule Reminder',
                          icon: <PiCalendarStar size={20} />,
                          callback: () => setShowReminderModal(true),
                          disabled: selectedDealIds.length !== 1,
                          disabledTooltipText:
                            'Please select exactly one deal to schedule a reminder',
                        },
                      ]
                    : []),
                  ...(featureFlag.enableSyndigoIntegration
                    ? [
                        {
                          label: 'Download Spec Sheets',
                          icon: <BsFileEarmarkPdf size={20} />,
                          callback: handleBulkDownloadSpecsheets,
                          disabled:
                            selectedDealIds.length > 20 ||
                            (downloadState.status !== 'idle' &&
                              downloadState.status !== 'completed' &&
                              downloadState.status !== 'failed'),
                          disabledTooltipText:
                            selectedDealIds.length > 20
                              ? 'Maximum 20 deals can be selected for spec sheet download'
                              : 'A download is already in progress',
                        },
                      ]
                    : []),
                  {
                    label: 'Assign Account Owner',
                    icon: <BsPerson size={20} />,
                    callback: () => setShowSelectAccountOwner(true),
                  },
                  {
                    label: 'Change Sales Stage',
                    icon: <BsSignpost size={20} />,
                    callback: () => setShowSelectSalesStage(true),
                  },
                  {
                    label: 'Manage Override',
                    icon: <BsPencil size={20} />,
                    callback: () => setShowOverrideModal(true),
                  },
                  {
                    label: 'Delete Deals',
                    icon: <BsXCircle size={20} />,
                    callback: () => bulkDeleteDeals(),
                  },
                ]}
                selectedRowsCount={totalSelectedRows}
                entityType="companies"
                disabled={isFetching || isDeleting}
              />
              <FilterChips
                identifier={IDENTIFIER}
                store={dealsFilterStore}
                showActive
                clearAllButton
              />
            </div>
          </S.SearchContainer>
        }
        key={props.tableKey}
        tableKey={props.tableKey}
        loading={isFetching || isDeleting}
        data={data?.results ?? []}
        columns={columns}
        sortableFields={sortableFields}
        virtualizeRows={false}
        enableRowSelection
        isPaginationEnabled={props.enablePagination}
        defaultSort={[{ id: 'annual_revenue', desc: true }]}
        paginationOptions={{
          totalRows: data?.count ?? 0,
          pageSize: PAGE_SIZE,
          setPage: setPage,
          page: page,
          isPaginationLoading: isFetching,
        }}
        stickyLastColumn
        expandColAccessor={'expanded-row'}
        selectAllText="Select Deals"
        extraCellColSpan={6}
        footerControls={
          <OverlayTrigger placement="top" overlay={tooltip}>
            <ExportButton
              variant="secondary"
              onClick={() => setShowExportModal(true)}
            >
              <BsDownload size={18} />
            </ExportButton>
          </OverlayTrigger>
        }
      />

      <EditDealModal
        onClose={() => setDealIdToEdit(undefined)}
        dealsTableQueryKey={TABLE_QUERY_KEY}
        show={!!dealIdToEdit}
        dealId={dealIdToEdit}
      />

      <DealExportModal
        tableTypeSlug="deals-table"
        count={data?.count ?? 0}
        show={showExportModal}
        _handleClose={() => setShowExportModal(false)}
        filters={filterAndSortParams}
        kind="deals_table"
      />

      <TagAssignModal
        show={showAssignTags}
        handleClose={() => setShowAssignTags(false)}
        tableQueryKey={TABLE_QUERY_KEY}
        data={data?.results ?? []}
        tableObjAccessor="contact_company.taglist"
        filterAndSortParams={filterAndSortParams}
        apiTagsAction={(values) => {
          return api.commonManageTags({
            values,
            tableType: 'deals-table',
          })
        }}
        idAccessor="contact_company.id"
      />

      <TagAssignModal
        show={showAssignDealTags}
        handleClose={() => setShowAssignDealTags(false)}
        tableQueryKey={TABLE_QUERY_KEY}
        data={data?.results ?? []}
        tableObjAccessor="deal_taglist"
        tagParent="deals"
        filterAndSortParams={filterAndSortParams}
        apiTagsAction={(values) => {
          return api.commonManageDealTags({
            values,
          })
        }}
        idAccessor="id"
      />

      <DistributorAssignModal
        show={showAssignDistributors}
        handleClose={() => setShowAssignDistributors(false)}
        tableQueryKey={TABLE_QUERY_KEY}
        data={data?.results ?? []}
        idAccessor="contact_company.id"
        tableObjAccessor="contact_company.distributors"
        filterAndSortParams={filterAndSortParams}
        apiDistributorsAction={(values) =>
          api.commonManageDistributors({
            tableType: 'deals-table',
            values,
          })
        }
      />

      <GPOAssignModal
        show={showAssignGPOs}
        handleClose={() => setShowAssignGPOs(false)}
        tableQueryKey={TABLE_QUERY_KEY}
        data={data?.results ?? []}
        idAccessor="contact_company.id"
        tableObjAccessor="contact_company.gpos"
        filterAndSortParams={filterAndSortParams}
        apiGPOsAction={(values) =>
          api.commonManageGPOs({
            tableType: 'deals-table',
            values,
          })
        }
      />

      <AccountOwnerSelectModal
        show={showSelectAccountOwner}
        handleClose={() => setShowSelectAccountOwner(false)}
        tableQueryKey={TABLE_QUERY_KEY}
        data={data?.results ?? []}
        idAccessor="id"
        filterAndSortParams={filterAndSortParams}
      />

      <SalesStageSelectModal
        show={showSelectSalesStage}
        handleClose={() => setShowSelectSalesStage(false)}
        tableQueryKey={TABLE_QUERY_KEY}
        data={data?.results ?? []}
        idAccessor="id"
        filterAndSortParams={filterAndSortParams}
      />

      <DealsOverrideModal
        show={showOverrideModal}
        handleClose={() => setShowOverrideModal(false)}
        tableQueryKey={TABLE_QUERY_KEY}
        data={data?.results ?? []}
        idAccessor="id"
        filterAndSortParams={filterAndSortParams}
      />

      {featureFlag.enableReminders && (
        <ReminderModal
          open={showReminderModal}
          onClose={() => setShowReminderModal(false)}
          defaultCompanyId={selectedCompanyId}
          defaultDealIds={selectedDealIds}
          onCreated={() => {
            setShowReminderModal(false)
          }}
        />
      )}
    </DataTableContainer>
  )
}
